import type { CapabilitySetType } from '@atlassian/forge-ui-types';
import { ExtensionEnvironment } from '@atlassian/forge-ui-types/extension';

import type { GQLExtension } from '../../graphql/types';

const extensionCache = new WeakMap<GQLExtension[], GQLExtension[]>();

const isStringArray = (value: unknown): value is string[] =>
	Array.isArray(value) && value.every((item) => typeof item === 'string');

/**
 * Most property values (`title`, `key`, `type`, `resource`, `icon`, …) are plain strings
 * rather than JSON, so parsing them unconditionally throws for the majority of the list —
 * around 1,100 caught `SyntaxError`s per pass on a real 143-app payload, which dominates
 * the transform.
 *
 * A JSON document can only open as an object, array, string or number, or be one of the
 * three bare literals. Anything else would have thrown and fallen back to the raw string,
 * so skipping the parse is equivalent.
 *
 * Property values that are not strings are kept verbatim for the same reason: stringifying
 * them for `JSON.parse` throws for anything but a bare literal, so the raw value survives.
 */
const canStartJsonDocument = (value: string): boolean => {
	const firstCharacter = value[0];

	return (
		firstCharacter === '{' ||
		firstCharacter === '[' ||
		firstCharacter === '"' ||
		firstCharacter === '-' ||
		(firstCharacter >= '0' && firstCharacter <= '9') ||
		value === 'true' ||
		value === 'false' ||
		value === 'null'
	);
};

const normalizeProperties = (properties: GQLExtension['properties']): Record<string, unknown> => {
	const normalizedProperties = properties.reduce(
		(acc: Record<string, unknown>, property: { key: string | number; value: string }) => {
			if (!property.value || !property.key) {
				return acc;
			}

			if (typeof property.value !== 'string' || !canStartJsonDocument(property.value.trim())) {
				acc[property.key] = property.value;
				return acc;
			}

			try {
				const jsonValue = JSON.parse(property.value);
				acc[property.key] = jsonValue;
			} catch (_error) {
				acc[property.key] = property.value;
			}
			return acc;
		},
		{},
	);

	if ('migratedFrom' in normalizedProperties && !isStringArray(normalizedProperties.migratedFrom)) {
		const { migratedFrom: _migratedFrom, ...propertiesWithoutInvalidMigratedFrom } =
			normalizedProperties;
		return propertiesWithoutInvalidMigratedFrom;
	}

	return normalizedProperties;
};

export const transformPreFilteredExtensions = (extensions?: GQLExtension[]): GQLExtension[] => {
	if (!extensions || extensions.length === 0) return [];
	if (extensionCache.has(extensions)) {
		return extensionCache.get(extensions)!;
	}

	const result = (extensions || []).flatMap((extension): GQLExtension[] => {
		if (!extension) {
			return [];
		}

		if (!extension.environmentKey) {
			return [];
		}

		return [
			{
				id: extension.id,
				appId: extension.appId,
				key: extension.key,
				environmentId: extension.environmentId,
				environmentType:
					extension.environmentType === ExtensionEnvironment.DEVELOPMENT ||
					extension.environmentType === ExtensionEnvironment.STAGING ||
					extension.environmentType === ExtensionEnvironment.PRODUCTION
						? extension.environmentType
						: ExtensionEnvironment.DEVELOPMENT,
				environmentKey: extension.environmentKey,
				properties: normalizeProperties(extension.properties),
				license: extension.license
					? {
							...extension.license,
							capabilitySet: mapCapabilitySet(extension.license.capabilitySet),
						}
					: null,
				userAccess: extension.userAccess
					? {
							hasAccess: extension.userAccess.hasAccess ?? false,
							enabled: extension.userAccess.enabled ?? false,
						}
					: null,
				type: extension.type,
				installationId: extension.installationId,
				appVersion: extension.appVersion ?? undefined,
				consentUrl: extension.consentUrl ?? undefined,
				migrationKey: extension.migrationKey ?? undefined,
				egress:
					extension.egress?.flatMap((egress) => {
						if (!egress.type || !egress.addresses) {
							return [];
						}

						return [
							{
								type: egress.type,
								addresses: egress.addresses?.flatMap((address) => address ?? []),
								category: egress.category ?? undefined,
								inScopeEUD: egress.inScopeEUD ?? undefined,
							},
						];
					}) ?? undefined,
				sandboxPolicies: extension.sandboxPolicies ?? undefined,
				scopes: extension.scopes ?? undefined,
				installationConfig:
					extension.installationConfig?.flatMap((config) => {
						if (!config?.key || config.value === null) {
							return [];
						}

						return [
							{
								key: config.key,
								value: config.value,
							},
						];
					}) ?? undefined,
				dataClassificationPolicyDecision: {
					status: extension.hiddenBy === 'APP_ACCESS_RULES' ? 'BLOCKED' : 'ALLOWED',
				},
			},
		];
	});

	const IS_TESTING_ENV =
		process.env.NODE_ENV === 'test' || process.env.JEST_WORKER_ID !== undefined;
	if (!IS_TESTING_ENV) {
		extensionCache.set(extensions, result);
	}

	return result;
};

const mapCapabilitySet = (capabilitySet: string): CapabilitySetType => {
	switch (capabilitySet) {
		case 'CAPABILITY_ADVANCED':
			return 'capabilityAdvanced';
		default:
			return 'capabilityStandard';
	}
};
